<script lang="ts">
    import { DRAWER_POSITION } from "@/constants/drawer";
    import { WISHLIST_WIDGET_NAMES } from "@/constants/widget-names";
    import WishlistTheme from "@apps/wishlist/components/WishlistTheme/WishlistTheme.svelte";
    import { WishlistsWidgetDefinition } from "@apps/wishlist/definitions";
    import { WishlistsStorefrontValuesBrandingEntity } from "@apps/wishlist/entities/WishlistsStorefrontValuesBrandingEntity";
    import WishlistsStorefrontValuesService from "@apps/wishlist/services/WishlistsStorefrontValuesService";
    import { saveWishlistModalToken } from "@apps/wishlist/tokens";
    import { WISHLISTS_SDK_METHODS } from "@apps/wishlist/wishlists_sdk/constants";
    import CloseWishlistsDrawerSdkMethodPayload from "@apps/wishlist/wishlists_sdk/methods/CloseWishlistsDrawerSdkMethodPayload";
    import OpenWishlistsDrawerSdkMethodPayload from "@apps/wishlist/wishlists_sdk/methods/OpenWishlistsDrawerSdkMethodPayload";
    import CustomizationManager from "@lib/CustomizationManager/CustomizationManager";
    import { DynamicImportManager } from "@modules/dynamic_import/DynamicImportManager";
    import WidgetClosedSdkEvent from "@modules/sdk/common_sdk/events/WidgetClosedSdkEvent";
    import WidgetInjectedSdkEvent from "@modules/sdk/common_sdk/events/WidgetInjectedSdkEvent";
    import WidgetOpenedSdkEvent from "@modules/sdk/common_sdk/events/WidgetOpenedSdkEvent";
    import {
        dispatchSdkEvent,
        subscribeToSdkMethod,
    } from "@modules/sdk/helpers";
    import {
        FocusTrapReturnType,
        focusTrap,
    } from "@ui-utils/actions/focusTrap";
    import { Observable } from "rxjs";
    import { skip, tap } from "rxjs/operators";
    import { onMount } from "svelte";

    import Drawer from "@components/Drawer/Drawer.svelte";
    import DrawerManager from "@components/Drawer/DrawerManager";
    import DynamicImport from "@components/DynamicImport/DynamicImport.svelte";
    import Icon from "@components/Icon.svelte";
    import CloseIcon from "@components/icons/CloseIcon.svelte";
    import { ModalManager } from "@components/Modal/ModalManager";
    import Portal from "@components/Portal/Portal.svelte";
    import Widget from "@components/Widget.svelte";
    import { LoggerInterface } from "@interfaces/LoggerInterface";

    import WishlistDrawerButton from "./components/WishlistDrawerButton.svelte";

    export let modalManager: ModalManager;
    export let customizationManager: CustomizationManager;
    export let widgetName: WISHLIST_WIDGET_NAMES;
    export let dynamicImportManager: DynamicImportManager;
    export let logger: LoggerInterface;
    export let wishlistsStorefrontValuesService: WishlistsStorefrontValuesService;
    export let showControls$: Observable<boolean>;
    export let afterWidgetInject: () => void;

    const drawerManager = new DrawerManager();
    const isDrawerVisible$ = drawerManager.visible$;
    const wishlistBranding$ =
        wishlistsStorefrontValuesService.selectWishlistsBranding();

    const handleOpenDrawer = () => {
        drawerManager.drawerShow();
    };

    const handleCloseDrawer = () => {
        drawerManager.drawerHide();
    };

    const saveWishlistModalSubscriber = () => {
        if (modalManager.isOpen(saveWishlistModalToken)) {
            handleCloseDrawer();
        }
    };

    modalManager.addModal(saveWishlistModalToken);
    modalManager.addSubscriber(
        saveWishlistModalToken,
        saveWishlistModalSubscriber
    );

    const openWishlistDrawerSubscription =
        subscribeToSdkMethod<OpenWishlistsDrawerSdkMethodPayload>(
            WISHLISTS_SDK_METHODS.OPEN_WISHLISTS_DRAWER,
            ({ onSuccess }) => {
                handleOpenDrawer();
                onSuccess();
            }
        );
    const closeWishlistDrawerSubscription =
        subscribeToSdkMethod<CloseWishlistsDrawerSdkMethodPayload>(
            WISHLISTS_SDK_METHODS.CLOSE_WISHLISTS_DRAWER,
            ({ onSuccess }) => {
                handleCloseDrawer();
                onSuccess();
            }
        );

    const drawerVisibilitySubscription = isDrawerVisible$
        .pipe(
            skip(1),
            tap((isVisible) => {
                if (isVisible) {
                    dispatchSdkEvent(new WidgetOpenedSdkEvent(widgetName));
                } else {
                    dispatchSdkEvent(new WidgetClosedSdkEvent(widgetName));
                }
            })
        )
        .subscribe();

    onMount(() => {
        afterWidgetInject();

        return () => {
            openWishlistDrawerSubscription.unsubscribe();
            closeWishlistDrawerSubscription.unsubscribe();
            modalManager.removeSubscriber(
                saveWishlistModalToken,
                saveWishlistModalSubscriber
            );
            drawerVisibilitySubscription.unsubscribe();
        };
    });

    const DIRECTION: Record<
        WishlistsStorefrontValuesBrandingEntity["wishlistDrawerAndPageSettings"]["drawerPosition"],
        DRAWER_POSITION
    > = {
        L: DRAWER_POSITION.LC,
        R: DRAWER_POSITION.RC,
    };

    let direction: DRAWER_POSITION;
    let focusTrapTargetRef: HTMLElement | null = null;
    let focusTrapInstance: FocusTrapReturnType;

    const onElementMount = (target: HTMLElement) => {
        if (focusTrapTargetRef) {
            focusTrapInstance = focusTrap(focusTrapTargetRef, {
                enabled: true,
                elements: [target],
                options: {
                    initialFocus: false,
                    allowOutsideClick: true,
                },
            });
        }
    };

    $: if ($wishlistBranding$) {
        direction =
            DIRECTION[
                $wishlistBranding$.wishlistDrawerAndPageSettings.drawerPosition
            ];
    }
    $: if (!$isDrawerVisible$) focusTrapInstance?.destroy();
</script>

{#if $wishlistBranding$}
    <WishlistTheme branding={$wishlistBranding$}>
        <DynamicImport
            let:value={{
                module,
                importModule,
            }}
            definition={WishlistsWidgetDefinition}
            shouldImportModule={$isDrawerVisible$}
            {dynamicImportManager}
        >
            <Drawer drawerClass="gw-wl-drawer" {direction} {drawerManager}>
                <Portal
                    order={2}
                    Component={WishlistDrawerButton}
                    componentProps={{
                        direction,
                        wishlistsStorefrontValuesService,
                    }}
                    componentListeners={{
                        click: handleOpenDrawer,
                        mouseenter: () => importModule(),
                    }}
                    selector=".gw-widget-placeholder-{direction}"
                    shadow
                    {widgetName}
                    {customizationManager}
                />

                <div
                    class="gw-wl-drawer__sidebar"
                    slot="sidebar"
                    bind:this={focusTrapTargetRef}
                >
                    <button
                        class="gw-wl-drawer-sidebar__close_btn"
                        on:click={handleCloseDrawer}
                        aria-label="Close wishlists sidebar"
                    >
                        <Icon>
                            <CloseIcon />
                        </Icon>
                    </button>

                    {#if $isDrawerVisible$ && module}
                        <Widget
                            class="gw-wl-widget-container"
                            widget={module}
                            params={[
                                {
                                    isWrappedWidget: true,
                                    isWidgetShadowRootEnabled: true,
                                    onElementMount,
                                    showControls$: showControls$,
                                },
                                logger,
                            ]}
                            on:widgetInit={() =>
                                dispatchSdkEvent(
                                    new WidgetInjectedSdkEvent(
                                        WISHLIST_WIDGET_NAMES.WISHLISTS_WIDGET
                                    )
                                )}
                        />
                    {/if}
                </div>
            </Drawer>
        </DynamicImport>
    </WishlistTheme>
{/if}

<style lang="postcss">
    :global {
        @import "../../../../themes/globals.css";
        @import "../../../../themes/default/default.css";

        .gw-wl-drawer {
            color: var(--gw-theme-main-text-primary);
            --gw-drawer-sidebar-width: 360px;
        }

        .gw-wl-widget-container,
        .gw-wl-widget-placeholder {
            height: 100%;
        }
    }

    .gw-wl-drawer__sidebar {
        height: 100%;
        position: relative;
    }

    .gw-wl-drawer-sidebar__close_btn {
        position: absolute;
        top: 0;
        right: 0px;
        width: 44px;
        height: 44px;
        padding: 14px;
        border: none;
        background: transparent;
        line-height: 1;
        cursor: pointer;
    }
</style>
